import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Section,
  SectionSubTitle,
  SwiperWithNavigation,
  SwiperSlide,
  LinkWithGAPromoClick,
  Typography,
  LinkWithGAClickButton,
} from 'common/components';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { usePromoViewEvent } from 'common/hooks';
import { ServiceCategory } from 'modules/service-categories/service-categories-types';
import { createElementKey } from 'utils';
import { ClickButtonElementName, analyticsUtils } from 'utils/analytics-utils';
import { getFinalInstoreData } from 'landings/services-main/services-main-utils';
import { KZ_INSTORE_SERVICES_LIST, INSTORE_SERVICES_LIST } from './services-instore-section-constants';
import {
  instoreSection,
  instoreTitle,
  instoreSwiper,
  instoreSlide,
  instoreItem,
  instoreItemImage,
  instoreItemContent,
  instoreItemName,
  instoreItemDescription,
} from './services-instore-section-styles';

type ServicesInstoreSectionProps = {
  serviceCategoriesList: ServiceCategory[] | null;
  regionId: string;
};

export const ServicesInstoreSection = ({ serviceCategoriesList, regionId }: ServicesInstoreSectionProps) => {
  const { t } = useTranslation();
  // Получаем список доступных в регионе инстор услуг
  const availableInstoreServices = getFinalInstoreData(
    regionId === KAZAKHSTAN ? KZ_INSTORE_SERVICES_LIST : INSTORE_SERVICES_LIST,
    serviceCategoriesList
  );

  const instoreRefs = useRef<HTMLAnchorElement[]>([]);
  const promotions = useMemo(
    () =>
      availableInstoreServices.map((service, index) => ({
        name: 'servicePage_bot',
        creative: service.serviceName,
        position: (index + 1).toString(),
      })),
    [availableInstoreServices]
  );

  usePromoViewEvent(instoreRefs, 'servicePage / store', promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      'servicePage / store',
      ClickButtonElementName.SwitchForward,
      'Услуги в магазинах'
    );
  };

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      'servicePage / store',
      ClickButtonElementName.SwitchBackward,
      'Услуги в магазинах'
    );
  };

  return (
    <Section className={instoreSection}>
      <SectionSubTitle className={instoreTitle} margin="m" variant="h3">
        <LinkWithGAClickButton
          className={instoreTitle}
          elementBlock="servicePage / store"
          elementName="Услуги в магазинах"
          elementText="Услуги в магазинах"
          title="Услуги в магазинах"
          href="/uslugi/uslugi-v-magazinah/">
          {t('instore-section.title')}
        </LinkWithGAClickButton>
      </SectionSubTitle>
      <SwiperWithNavigation
        className={instoreSwiper}
        slidesPerView="auto"
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}>
        {availableInstoreServices.map((service, index) => (
          <SwiperSlide className={instoreSlide} key={createElementKey('instore-service', index)}>
            <LinkWithGAPromoClick
              className={instoreItem}
              title={service.serviceName}
              href={service.serviceUrl}
              elementBlock="servicePage / store"
              position={index}
              promotion={promotions[index]}
              bannerRefs={instoreRefs}>
              <img
                className={instoreItemImage}
                src={service.serviceImage || ''}
                alt={service.serviceName}
                title={service.serviceName}
                width="112"
                height="80"
              />
              <div className={instoreItemContent}>
                <Typography className={instoreItemName} variant="m" size="body_long">
                  {service.serviceName}
                </Typography>
                <Typography className={instoreItemDescription} variant="caption" color="text-secondary">
                  {service.serviceDescription}
                </Typography>
              </div>
            </LinkWithGAPromoClick>
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>
    </Section>
  );
};
